import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { AltaDeInvitacionDemo, InvitacionDemo } from '../types/admin.types';

/** Quién se dio de alta con una invitación: empresa, persona, correo y fecha. */
export function AltasDeInvitacionDialog({
  invitacion,
  onCerrar,
}: {
  /** `null` = cerrado. */
  invitacion: InvitacionDemo | null;
  onCerrar: () => void;
}) {
  const [altas, setAltas] = useState<AltaDeInvitacionDemo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  const id = invitacion?.id;
  // Recuerda el nombre mientras el diálogo se cierra, para que el título no quede vacío.
  const [nombreVisible, setNombreVisible] = useState('');
  useEffect(() => {
    if (invitacion) setNombreVisible(invitacion.nombre);
  }, [invitacion]);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    setAltas(null);
    setError(null);
    adminService
      .getAltasDeInvitacionDemo(id)
      .then((data) => {
        if (!cancelado) setAltas(data);
      })
      .catch((e) => {
        if (!cancelado) {
          setError(mensajeDeError(e, 'No se pudieron leer las altas. Probá de nuevo.'));
        }
      });
    return () => {
      cancelado = true;
    };
  }, [id, intento]);

  return (
    <Dialog open={invitacion !== null} onOpenChange={(o) => !o && onCerrar()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Altas de «{invitacion?.nombre ?? nombreVisible}»</DialogTitle>
          <DialogDescription>
            Los comercios que entraron a la demo con este enlace, el más reciente primero.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" onClick={() => setIntento((n) => n + 1)}>
              Reintentar
            </Button>
          </div>
        ) : altas === null ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : altas.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Todavía nadie se dio de alta con esta invitación.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Persona</TableHead>
                  <TableHead>Correo</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {altas.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      {a.empresaExiste && a.tenantId ? (
                        <Link
                          to={`/plataforma/empresas/${a.tenantId}`}
                          className="text-primary hover:underline"
                        >
                          {a.empresa}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">
                          {a.empresa} <span className="text-xs">(eliminada)</span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{a.persona}</TableCell>
                    <TableCell className="text-sm">{a.correo}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(a.createdAt).toLocaleString('es-BO', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
